import React, { useEffect, useState, useCallback, useRef } from "react"
import { useDispatch } from "react-redux"
import { Download, X } from "lucide-react"
import toast from "react-hot-toast"
import AdminCheckInForm, {
  EMPTY_CHECK_IN_FORM,
  mapCheckInToForm,
  EMPTY_END_OF_YEAR_FORM,
  mapEndOfYearCheckInToForm,
} from "../../Components/admin/AdminCheckInForm"
import { uploadCheckInEvidence } from "../../redux/adminCheckInSlice"
import {
  buildFormPdfFilename,
  downloadFormAsPdf,
} from "../../utils/downloadFormPdf"
import FormHeaderLogo from "../../Components/admin/FormHeaderLogo"

const getEmptyForm = (checkInType, adminName) => {
  if (checkInType === "end_of_year") {
    return {
      ...EMPTY_END_OF_YEAR_FORM,
      leaderName: adminName || "",
    }
  }

  return {
    ...EMPTY_CHECK_IN_FORM,
    leaderName: adminName || "",
  }
}

const mapCheckInForm = (checkIn, checkInType) => {
  if (checkInType === "end_of_year" || checkIn?.checkInType === "end_of_year") {
    return mapEndOfYearCheckInToForm(checkIn)
  }

  return mapCheckInToForm(checkIn)
}

const buildAdminPayload = (formData, checkInType) => {
  if (checkInType === "end_of_year") {
    return {
      leaderName: formData.leaderName,
      leaderLocation: formData.leaderLocation,
      impactOnPractice: formData.impactOnPractice,
      impactOnStudentLearning: formData.impactOnStudentLearning,
      challengesAndNextSteps: formData.challengesAndNextSteps,
      evidenceAttachments: formData.evidenceAttachments || [],
      reflectionDate: formData.reflectionDate || null,
    }
  }

  return {
    leaderName: formData.leaderName,
    leaderLocation: formData.leaderLocation,
    selfReflection: formData.selfReflection,
    evidenceAttachments: formData.evidenceAttachments || [],
  }
}

const AdminCheckInModal = ({
  isOpen,
  onClose,
  mode = "initiate",
  checkInType = "mid_year",
  adminName,
  checkIn,
  onInitiate,
  onAdminDraftSave,
  onAdminSubmit,
  onConferenceSubmit,
  onReinitiate,
  loading = false,
}) => {
  const dispatch = useDispatch()
  const [formData, setFormData] = useState(() => getEmptyForm(checkInType, adminName))
  const [uploadingEvidence, setUploadingEvidence] = useState(false)
  const [downloadingPdf, setDownloadingPdf] = useState(false)
  const initializedRef = useRef(false)
  const formContentRef = useRef(null)

  useEffect(() => {
    if (!isOpen) {
      initializedRef.current = false
      return
    }

    if (!initializedRef.current) {
      if (checkIn) {
        setFormData(mapCheckInForm(checkIn, checkInType))
      } else {
        setFormData(getEmptyForm(checkInType, adminName))
      }
      initializedRef.current = true
    }
  }, [isOpen, checkIn, adminName, checkInType])

  const handleChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }, [])

  const handleUploadEvidence = async (files) => {
    try {
      setUploadingEvidence(true)
      const uploaded = await dispatch(uploadCheckInEvidence(files)).unwrap()
      setFormData((prev) => ({
        ...prev,
        evidenceAttachments: [...(prev.evidenceAttachments || []), ...uploaded],
      }))
    } catch {
      // toast handled in slice
    } finally {
      setUploadingEvidence(false)
    }
  }

  const handleSaveDraft = () => {
    if (mode === "admin") {
      onAdminDraftSave?.(buildAdminPayload(formData, checkInType))
    }
  }

  const handleSubmit = () => {
    if (mode === "initiate") {
      onInitiate?.()
      return
    }

    if (mode === "admin") {
      onAdminSubmit?.(buildAdminPayload(formData, checkInType))
      return
    }

    if (mode === "super_admin_conference") {
      if (checkInType === "end_of_year") {
        onConferenceSubmit?.({
          conferenceDate: formData.conferenceDate || null,
          summativeFeedback: formData.summativeFeedback,
          developmentImpact: formData.developmentImpact,
          partnerImpact: formData.partnerImpact,
          communityImpact: formData.communityImpact,
          evaluativeCycleComplete: formData.evaluativeCycleComplete,
          tier2Support: formData.tier2Support,
          tier2Strategies: formData.tier2Strategies,
          multiYearContinue: formData.multiYearContinue,
          multiYearAdjust: formData.multiYearAdjust,
          multiYearCompleted: formData.multiYearCompleted,
          multiYearNotes: formData.multiYearNotes,
        })
        return
      }

      onConferenceSubmit?.({
        conferenceDate: formData.conferenceDate || null,
        feedbackToLeader: formData.feedbackToLeader,
      })
    }
  }

  const getSubmitLabel = () => {
    if (mode === "initiate") {
      return checkInType === "mid_year"
        ? "Initiate Mid-Year Check-in"
        : "Initiate End-of-Year Reflection"
    }
    if (mode === "admin") return "Submit to Super Admin"
    if (mode === "super_admin_conference") return "Complete Conference"
    return "Close"
  }

  const getTitle = () =>
    checkInType === "mid_year" ? "Mid-Year Check-in" : "End-of-Year Reflection"

  const handleDownloadPdf = async () => {
    if (!formContentRef.current) return

    try {
      setDownloadingPdf(true)
      const filename = buildFormPdfFilename({
        title:
          checkInType === "mid_year"
            ? "Mid-Year-Check-in"
            : "End-of-Year-Reflection",
        adminName,
        year: checkIn?.year,
      })
      await downloadFormAsPdf(formContentRef.current, filename)
      toast.success("PDF downloaded")
    } catch (error) {
      toast.error(error?.message || "Failed to download PDF")
    } finally {
      setDownloadingPdf(false)
    }
  }

  const canDownloadPdf = mode === "view" && checkIn?.status === "completed"

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[60] p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[95vh] flex flex-col shadow-xl">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-bold text-[#241763]">{getTitle()}</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100"
            aria-label="Close check-in modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div ref={formContentRef}>
            <FormHeaderLogo />

            {mode === "initiate" && (
              <p className="text-sm text-gray-600 my-4">
                Initiate this{" "}
                {checkInType === "mid_year"
                  ? "mid-year check-in"
                  : "end-of-year reflection"}{" "}
                form for <span className="font-semibold">{adminName}</span>. The
                leader can complete it from their account settings dashboard.
              </p>
            )}

            {mode === "admin" && (
              <>
                {checkIn?.reinitiatedAt && (
                  <div
                    className="my-4 rounded border border-[#ED7D31] bg-[#FDE9D9] px-3 py-2 text-sm text-[#833C0C]"
                    role="status"
                  >
                    This form was{" "}
                    <span className="font-semibold">reinitiated</span> by Super
                    Admin. Please review your answers, update if needed, and
                    resubmit.
                  </div>
                )}
                <p className="text-sm text-gray-600 my-4">
                  Fill in any fields and click Save Draft to keep your progress.
                  When everything is ready, submit to Super Admin. The conference
                  section will be completed by the Super Admin.
                </p>
              </>
            )}

            <AdminCheckInForm
              formData={formData}
              onChange={handleChange}
              mode={mode}
              checkInType={checkInType}
              adminName={adminName}
              onUploadEvidence={handleUploadEvidence}
              uploadingEvidence={uploadingEvidence}
              record={checkIn}
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 p-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
          >
            {mode === "view" ? "Close" : "Cancel"}
          </button>

          {canDownloadPdf && (
            <button
              type="button"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              className="px-4 py-2 text-sm border border-[#241763] text-[#241763] rounded hover:bg-[#241763]/5 disabled:opacity-50 flex items-center gap-2"
              aria-label="Download form as PDF"
            >
              <Download className="w-4 h-4" />
              {downloadingPdf ? "Preparing PDF..." : "Download PDF"}
            </button>
          )}

          {mode === "admin" && (
            <button
              type="button"
              onClick={handleSaveDraft}
              disabled={loading || uploadingEvidence}
              className="px-4 py-2 text-sm border border-[#241763] text-[#241763] rounded hover:bg-[#241763]/5 disabled:opacity-50"
              aria-label="Save draft"
            >
              {loading ? "Please wait..." : "Save Draft"}
            </button>
          )}

          {mode === "super_admin_conference" && onReinitiate && (
            <button
              type="button"
              onClick={() => {
                onReinitiate()
                onClose()
              }}
              disabled={loading}
              className="px-4 py-2 text-sm bg-[#ED7D31] text-white rounded hover:opacity-90 disabled:opacity-50"
              aria-label="Reinitiate check-in"
            >
              {loading ? "Please wait..." : "Reinitiate"}
            </button>
          )}

          {mode !== "view" && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || uploadingEvidence}
              className="px-4 py-2 text-sm bg-[#241763] text-white rounded hover:bg-[#1a1047] disabled:opacity-50"
            >
              {loading ? "Please wait..." : getSubmitLabel()}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default AdminCheckInModal
